<script setup>
import {
  StealieButton,
  StealieHeading,
  StealieOverline,
  StealieTextInput,
  StealieParagraph,
  StealieLink,
} from "@get-overalls/fe-stealie";
import SignInLayout from "@/layouts/SignInLayout.vue";
import { useSignIn } from "./composables/useSignIn";
import FormError from "@/components/FormError.vue";

const {
  formData,
  errors,
  submitError,
  isSubmitting,
  mfaRequired,
  mfaCode,
  handleSignInSubmit,
  handleMfaSubmit,
  resendCode,
} = useSignIn();
</script>

<template>
  <SignInLayout>
    <div data-sr-unmask>
      <div>
        <StealieOverline>Sign In</StealieOverline>
      </div>
      <div class="col-span-4">
        <StealieHeading size="lg" class="mb-6 text-balance">
          We've got you covered!
        </StealieHeading>
      </div>
      <div v-if="!mfaRequired">
        <form
          aria-label="Sign in form"
          method="post"
          novalidate
          class="flex flex-col gap-6"
          @submit.prevent="handleSignInSubmit"
        >
          <div>
            <StealieTextInput
              v-model="formData.email"
              name="email"
              type="email"
              label="Work Email"
              autocomplete="username"
              required
              :error-message="errors.email"
            />
          </div>
          <div>
            <StealieTextInput
              v-model="formData.password"
              name="password"
              type="password"
              label="Password"
              autocomplete="current-password"
              required
              :error-message="errors.password"
            />
          </div>
          <FormError v-if="submitError" :error="submitError" />
          <div>
            <StealieButton
              type="submit"
              class="mt-2 mb-6"
              full-width
              :loading="isSubmitting"
            >
              Sign in
            </StealieButton>
          </div>
        </form>
        <div class="flex flex-col gap-y-4">
          <StealieParagraph size="sm">
            New to Overalls?
            <StealieLink :to="{ name: 'SignUp' }" underline>Sign up</StealieLink>
          </StealieParagraph>
          <StealieParagraph size="sm">
            <StealieLink :to="{ name: 'ForgotPassword' }" underline>
              Forgot password?
            </StealieLink>
          </StealieParagraph>
          <StealieParagraph size="sm">
            Need help?
            <StealieLink to="mailto:help@getoveralls.com" underline external-link>
              Contact support
            </StealieLink>
          </StealieParagraph>
        </div>
      </div>
      <div v-else>
        <StealieParagraph id="mfa-description" class="mb-4">
          We sent a verification code to your phone number on file. Please enter it below
          to continue signing in.
        </StealieParagraph>
        <form
          aria-label="MFA verification form"
          method="post"
          novalidate
          class="flex flex-col gap-6"
          @submit.prevent="handleMfaSubmit"
        >
          <div>
            <StealieTextInput
              v-model="mfaCode"
              name="mfaVerificationCode"
              type="text"
              inputmode="numeric"
              label="Verification Code"
              aria-describedby="mfa-description"
              autocomplete="one-time-code"
              autofocus
              required
              :error-message="errors.mfaVerificationCode"
            />
          </div>
          <FormError v-if="submitError" :error="submitError" />
          <div>
            <StealieButton
              type="submit"
              class="mt-2 mb-6"
              full-width
              :loading="isSubmitting"
            >
              Verify
            </StealieButton>
          </div>
        </form>
        <div>
          <StealieParagraph size="sm" class="mb-4">
            Haven't received the code?
            <StealieButton variant="tertiary" @click="resendCode"
              >Resend code</StealieButton
            >
          </StealieParagraph>
        </div>
      </div>
    </div>
  </SignInLayout>
</template>
